import { useState } from 'react';
import { BookOpen, X } from 'lucide-react';
import { GRADE_LEVELS } from '@/constants/gradeLevels';
import { CURRICULA } from '@/constants/curricula';
import { cn } from '@/lib/utils';

const COURSE_ONLY_FROM_SORT_KEY = 7;

export function CurriculumByGrade() {
  const [selectedId, setSelectedId] = useState(null);

  const sortedGrades = Object.entries(GRADE_LEVELS).sort(
    ([, a], [, b]) => a.sortKey - b.sortKey
  );

  const selectedCurriculum = selectedId ? CURRICULA[selectedId] : null;
  const selectedGrade = selectedId ? GRADE_LEVELS[selectedId] : null;
  const selectedShowsGrade =
    selectedGrade && selectedGrade.sortKey < COURSE_ONLY_FROM_SORT_KEY;
  const selectedPrimaryTitle = selectedGrade
    ? selectedShowsGrade
      ? selectedGrade.label
      : selectedGrade.course
    : null;

  const lessons = selectedCurriculum
    ? selectedCurriculum.sections.flatMap((s) => s.lessons)
    : [];

  return (
    <div className="mt-10">
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        {sortedGrades.map(([subjectId, gradeInfo]) => {
          const isActive = selectedId === subjectId;
          const showsGrade = gradeInfo.sortKey < COURSE_ONLY_FROM_SORT_KEY;
          const panelId = 'curriculum-panel';
          return (
            <li key={subjectId}>
              <button
                type="button"
                onClick={() =>
                  setSelectedId((prev) =>
                    prev === subjectId ? null : subjectId
                  )
                }
                aria-pressed={isActive}
                aria-controls={panelId}
                className={cn(
                  'group flex w-full cursor-pointer items-center justify-center rounded-2xl border-2 px-3 py-4 transition-all duration-200 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 sm:h-full sm:px-4 sm:py-5',
                  isActive
                    ? '-translate-y-0.5 border-transparent bg-linear-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/25'
                    : 'border-slate-200 bg-white text-slate-900 hover:-translate-y-0.5 hover:border-brand-300 hover:bg-brand-50/40 hover:shadow-md'
                )}
              >
                {showsGrade ? (
                  <span className="flex flex-col items-center text-center leading-tight">
                    <span className="text-base font-extrabold tracking-tight sm:text-lg">
                      {gradeInfo.label}
                    </span>
                    <span
                      className={cn(
                        'mt-1 text-[11px] font-medium sm:text-xs md:text-sm',
                        isActive ? 'text-brand-100' : 'text-slate-500'
                      )}
                    >
                      ({gradeInfo.course})
                    </span>
                  </span>
                ) : (
                  <span className="text-base font-extrabold tracking-tight sm:text-lg">
                    {gradeInfo.course}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {selectedCurriculum && (
        <div
          id="curriculum-panel"
          role="region"
          aria-label={`${selectedPrimaryTitle} curriculum`}
          className="mt-6 overflow-hidden rounded-3xl border border-brand-200 bg-white shadow-xl ring-1 ring-slate-900/5 sm:mt-8"
        >
          <div className="relative bg-linear-to-br from-brand-500 to-brand-700 px-6 py-5 sm:px-8 sm:py-6">
            <div
              className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.18),transparent_60%)]"
              aria-hidden="true"
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-100 sm:text-sm">
                  Curriculum · 20 classes
                </p>
                <h3 className="mt-1 text-xl font-extrabold tracking-tight text-white sm:text-2xl">
                  {selectedPrimaryTitle}
                </h3>
                {selectedShowsGrade && (
                  <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white ring-1 ring-inset ring-white/30 sm:text-sm">
                    <BookOpen className="size-3.5 sm:size-4" aria-hidden="true" />
                    {selectedGrade.course}
                  </span>
                )}
                {selectedCurriculum.overview && (
                  <p className="mt-3 text-sm leading-relaxed text-brand-50 sm:text-base">
                    {selectedCurriculum.overview}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                aria-label="Close curriculum panel"
                className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <ol className="divide-y divide-slate-100">
            {lessons.map((lesson, i) => (
              <li
                key={lesson.id}
                className="flex items-center gap-4 px-6 py-3 transition-colors hover:bg-brand-50/40 sm:px-8"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-brand-50 to-brand-100 text-xs font-extrabold text-brand-700 ring-1 ring-brand-200">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-sm font-semibold text-slate-900 sm:text-base">
                  {lesson.title}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}